import { useMemo } from 'react';
import { useSelector } from '../redux/store';
import { isAssignableUser } from '../utils/assignableUsers';
import { hasPractitionerRole } from '../utils/practiceUserRoles';

/**
 * Shared hook to derive practitioners and assignable users from Redux practiceUser state.
 * Eliminates duplicated useMemo logic across TreatmentAndAppointment, AssignedToLead, etc.
 */
const usePracticeStaff = () => {
    const { practiceUser } = useSelector((state) => state.practiceUser);

    const practitioners = useMemo(() => {
        if (!practiceUser?.results?.length) return [];
        return practiceUser.results
            .filter(el => el?.is_enabled && hasPractitionerRole(el) && !el?.user?.is_superuser)
            .map(el => ({
                ...el,
                _name: `${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`.trim(),
                _initials: ((el?.user?.first_name?.[0] || '') + (el?.user?.last_name?.[0] || '')).toUpperCase() || '?',
            }));
    }, [practiceUser]);

    const assignableUsers = useMemo(() => {
        if (!practiceUser?.results?.length) return [];
        return practiceUser.results
            // el?.user?.is_active === false excludes only an EXPLICITLY deactivated
            // account. The BE field is being added in a parallel PR — when it's
            // absent/undefined on older payloads, the user is kept (fail-open),
            // matching the coordinator's decision. Without this, a deactivated
            // user still shows as an "Assign to all" avatar and the subsequent
            // bulk-assignees write 400s ("invalid_user_ids"), silently dropping
            // the lead-propagation dialog (pre-prod, practice 36).
            // The rule itself lives in utils/assignableUsers so the Dentally
            // booking screens, which read a practice-scoped selector rather than
            // this slice, can apply exactly the same test.
            .filter(isAssignableUser)
            .map(el => ({
                ...el,
                _name: `${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`.trim(),
                _initials: ((el?.user?.first_name?.[0] || '') + (el?.user?.last_name?.[0] || '')).toUpperCase() || '?',
            }));
    }, [practiceUser]);

    /**
     * Broader assignable list for the TASK context (internal ops UI).
     * Includes all enabled practice users regardless of role or superuser status.
     * Do NOT use this for lead-facing dropdowns — use assignableUsers there.
     */
    const taskAssignableUsers = useMemo(() => {
        if (!practiceUser?.results?.length) return [];
        return practiceUser.results
            // Same is_active exclusion as assignableUsers — see comment there.
            .filter(el => el?.is_enabled && el?.user?.is_active !== false)
            .map(el => ({
                ...el,
                _name: `${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`.trim(),
                _initials: ((el?.user?.first_name?.[0] || '') + (el?.user?.last_name?.[0] || '')).toUpperCase() || '?',
            }));
    }, [practiceUser]);

    const bookableStaff = useMemo(() => {
        if (practitioners.length > 0) return practitioners;
        if (!practiceUser?.results) return [];
        return practiceUser.results
            .filter(el => {
                const role = el?.role?.toUpperCase();
                return el?.is_enabled && role !== 'ADMIN' && role !== 'SUPPORT' && !el?.user?.is_superuser;
            })
            .map(el => ({
                ...el,
                _name: `${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`.trim(),
                _initials: ((el?.user?.first_name?.[0] || '') + (el?.user?.last_name?.[0] || '')).toUpperCase() || '?',
            }));
    }, [practitioners, practiceUser]);

    return { practitioners, assignableUsers, taskAssignableUsers, bookableStaff, isLoaded: !!practiceUser?.results };
};

export default usePracticeStaff;
